/**
 * Шторка «Смена» — центр отчётности кассы.
 *
 * Отсюда печатаются X-отчёт и Акт реализации текущей смены,
 * открывается список прошлых смен (Z-копия и акт любой из них —
 * старшему), и отсюда же уходят в закрытие смены. Сами формы
 * строит ядро по агрегату сервера: здесь только кнопки и печать.
 */
import { useEffect, useState } from 'react';
import type { ShiftBrief } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';

export function ShiftSheet({
  shiftNumber, openedAt, cashier, senior, online,
  onX, onAct, onDayX, onDayAct, onPastX, onPastZ, onPastAct,
  onClose, onCloseShift, loadRecent,
}: {
  shiftNumber: number | null;
  openedAt: string | null;
  cashier: string;
  senior: boolean;
  online: boolean;
  onX: () => void;
  onAct: () => void;
  onDayX: (dateISO: string) => void;
  onDayAct: (dateISO: string) => void;
  onPastX: (s: ShiftBrief) => void;
  onPastZ: (s: ShiftBrief) => void;
  onPastAct: (s: ShiftBrief) => void;
  onClose: () => void;
  onCloseShift: () => void;
  loadRecent: () => Promise<ShiftBrief[]>;
}) {
  const [past, setPast] = useState<ShiftBrief[] | null>(null);
  // Отчёт за день: по умолчанию вчера — самый частый вопрос владельца
  const [dayDate, setDayDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [pastOpen, setPastOpen] = useState(false);
  useEffect(() => {
    if (!pastOpen || past !== null) return;
    void loadRecent().then(setPast).catch(() => setPast([]));
  }, [pastOpen, past, loadRecent]);

  const opened = openedAt
    ? new Date(openedAt).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true"
      aria-label="Смена" onClick={onClose}>
      <div className="dialog shift-sheet" onClick={(e) => e.stopPropagation()}>
        <span className="eyebrow">Смена</span>
        <h1>{shiftNumber ? `Смена №${shiftNumber}` : 'Смена'}</h1>
        {opened && <p className="hint">Открыта {opened} · Кассир: {cashier}</p>}
        {!online && <p className="hint">Нет связи: отчёты требуют сервера</p>}

        <div className="shift-actions">
          <button className="btn" disabled={!online} onClick={onX}>
            🖨 X-отчёт — за сегодня
          </button>
          <button className="btn" disabled={!online} onClick={onAct}>
            🖨 Акт реализации — за сегодня
          </button>
          <p className="hint">Z-отчёт этой смены печатается при её
            закрытии — кнопка внизу. Закрытым сменам Z-копия доступна
            в списке ниже.</p>
          <button className="btn ghost" disabled={!online || !senior}
            title={senior ? 'Z-копии и акты прошлых смен' : 'Прошлые смены — старшему'}
            onClick={() => setPastOpen((v) => !v)}>
            {pastOpen ? '▾' : '▸'} Прошлые смены
          </button>
        </div>

        <div className="day-report">
          <span className="day-label">Отчёт за день</span>
          <input type="date" className="day-input" value={dayDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDayDate(e.target.value)} />
          <button className="btn small" disabled={!online}
            onClick={() => onDayX(dayDate)}>🖨 X</button>
          <button className="btn small" disabled={!online}
            onClick={() => onDayAct(dayDate)}>🖨 Акт</button>
        </div>
        <p className="hint">День считается по утренней границе заведения
          (обычно 06:00), а не по полуночи: ночные чеки остаются
          в своём дне. Z за день не бывает — он гасит смену.</p>

        {pastOpen && (
          <div className="past-shifts">
            {past === null && <p className="hint">Загружаю…</p>}
            {past?.length === 0 && <p className="hint">Смен пока нет</p>}
            {past?.map((s) => (
              <div key={s.id} className="past-shift-card">
                <div className="psc-info">
                  <span className="psc-title">
                    Смена №{s.number}
                    {s.closedAt ? '' : <em> · открыта</em>}
                  </span>
                  <span className="psc-sub">
                    {new Date(s.openedAt).toLocaleDateString('ru-RU',
                      { day: '2-digit', month: '2-digit' })}
                    {' · чеков '}{s.checks}{' · '}{formatMoney(s.revenue)}
                  </span>
                </div>
                <div className="psc-btns">
                  <button className="btn" onClick={() => onPastX(s)}>X</button>
                  <button className="btn" disabled={!s.closedAt}
                    title={s.closedAt ? 'Копия Z' : 'Z появится после закрытия'}
                    onClick={() => onPastZ(s)}>Z</button>
                  <button className="btn" onClick={() => onPastAct(s)}>Акт</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="dialog-actions">
          <button className="btn ghost" onClick={onClose}>Закрыть окно</button>
          <button className="btn accent" onClick={onCloseShift}>
            Закрыть смену (Z-отчёт) →
          </button>
        </div>
      </div>
    </div>
  );
}
